import { Typography } from '@arco-design/web-react';
import { getIntentName } from '../utils/intentName';
import { fmtTime } from '../utils/format';
import { emotionMeta } from '../utils/labels';
import { statusLabel } from './StatusTag';
import PillTag, { type PillColor } from './PillTag';

function senderOf(m: any): string {
  return m?.sender_type || m?.senderType || 'visitor';
}

// parseKbRef 机器人直答的来源引用（chat_message.kb_ref 存 JSON 文本；字段名沿用历史口径，
// 内容随知识来源变更——自建问答库下线后由 WeKnora 提供出处）。
// WS 实时载荷可能已解析为对象。解析失败/空返回 null，调用方按字段展示兜底。
function parseKbRef(m: any): { qa_id?: number; doc_title?: string; version?: number; effective_at?: string } | null {
  const raw = m?.kb_ref;
  if (!raw) return null;
  if (typeof raw !== 'string') return raw || null;
  try {
    const v = JSON.parse(raw);
    return v && typeof v === 'object' ? v : null;
  } catch {
    return null;
  }
}

// —— 附件打开方式收口（后端「内联白名单 = 图片(排除 SVG)/音频/视频，其余 attachment + nosniff」的前端配套）——
// 要点：文件类附件不再当「同源可导航链接」交给浏览器顶层打开（访客可控内容会在管理台 origin 里
// 变成顶层文档）；同源用 `download` 钉住下载语义，跨域 `download` 会被忽略，只补 rel 让外链带走 opener。

/** 附件 URL 是否跨域（以解析后的绝对 origin 判定；`download` 只在同源或允许 CORS 的 URL 生效） */
function isCrossOriginUrl(href: string): boolean {
  if (!href) return false;
  try {
    return new URL(href, window.location.href).origin !== window.location.origin;
  } catch {
    return false; // 解析失败按同源处理：最坏是浏览器忽略 download，不会放大权限
  }
}

/** 字符串是否 SVG 后缀（.svg / .svgz，先去掉 query/hash） */
function hasSvgSuffix(v: string): boolean {
  return /\.svgz?$/.test(String(v || '').split(/[?#]/)[0].trim().toLowerCase());
}

/**
 * 是否 SVG 附件：后端返回的 mime 与「对象 key / 文件名 / content（图片与文件的 content 即文件名）」
 * 两路线索，任一命中即按文件处理——SVG 是矢量文档（可含脚本/外链），后端同批已把它移出内联白名单，
 * 前端不按图片渲染，避免两端口径不一。
 */
function isSvgAttachment(m: any, key: string): boolean {
  const mime = String(m?.mime_type || m?.mimeType || m?.content_type || m?.contentType || '').toLowerCase();
  if (mime.includes('svg')) return true;
  return [key, m?.file_name, m?.fileName, m?.name, m?.content].some((v) => hasSvgSuffix(String(v || '')));
}

/**
 * 会话单条消息气泡（微信式左右布局，工作台在线会话 + 会话记录/客户动态会话详情共用）：
 * 支持 text / image / file / voice / video 富媒体，访客侧情绪/意图标签。
 * 注：chat-bubble* 基础样式已在 styles/components.css（全局），无需页面级 CSS。
 *
 * `variant="transcript"`：工作台「本会话对话」用**与通话转写同一套气泡**（客户蓝 / 坐席绿 / 机器人青，
 * 名字+时间在气泡上沿、不再用微信式自有配色），两页看起来是同一套版式；会话记录等其它场景仍用默认形态。
 *
 * 原「转到知识检索」钮（访客气泡后的转出入口）随检索条整条下线一并移除。
 */
export default function ChatMessageBubble({ m, variant = 'default' }: { m: any; variant?: 'default' | 'transcript' }) {
  const sender = senderOf(m);
  const content = m?.content || '';
  // 统一时间：兼容历史 created_at / WS 实时载荷 ts(ms) / createdAt
  const time = fmtTime(m?.created_at || m?.createdAt || m?.ts, 'HH:mm:ss');
  const isAgent = sender === 'agent';
  const isSystem = sender === 'system';
  const isRight = isAgent || sender === 'robot';
  // chat_message 载荷含 agent_name：坐席气泡优先展示坐席姓名
  const senderName = isAgent ? m?.agent_name || m?.agentName || statusLabel(sender) : statusLabel(sender) || sender;
  // 默认气泡底色：坐席=品牌主色、机器人=AI 电光青，其余用中性填充 token（禁硬编码色）
  const colors: Record<string, string> = {
    agent: 'var(--bcc-primary)',
    robot: 'var(--bcc-ai)',
    visitor: 'var(--color-fill-3)',
    user: 'var(--color-fill-3)',
    system: 'var(--color-fill-2)',
  };
  if (isSystem) {
    return (
      <div style={{ textAlign: 'center', margin: '8px 0' }}>
        <Typography.Text type="secondary" style={{ fontSize: 12 }}>
          {content} {time}
        </Typography.Text>
      </div>
    );
  }
  const msgType = m?.type || m?.msgType || 'text';
  const contentUrl = m?.content_url || m?.contentUrl || '';
  const assetUrl = contentUrl ? `/api/v1/chat/attachments/${encodeURIComponent(contentUrl)}` : '';
  // 图片（非 SVG）仍走 <img>——img 上下文不执行脚本、也不是顶层文档；其余一律走显式下载语义
  const assetIsImage = msgType === 'image' && !!assetUrl && !isSvgAttachment(m, contentUrl);
  const assetCrossOrigin = isCrossOriginUrl(assetUrl);
  // 机器人直答的来源/版本行（坐席侧追溯；出处取自 chat_message.kb_ref，与访客侧对外来源卡同一字段）
  const kbRef = parseKbRef(m);
  // 在线客服坐席辅助：访客消息情绪/意图标签（后端 AnalyzeRealtime 落库）；情绪文案与语义色单源 labels.ts
  const emotion = m?.emotion || '';
  const [emotionText, emotionColor] = emotionMeta(emotion);
  const intentCode = m?.intent_code || '';

  /** 消息正文（两种气泡形态共用） */
  const body = assetIsImage ? (
    <img src={assetUrl} alt={content || '图片'} style={{ maxWidth: 260, maxHeight: 200, borderRadius: 6, display: 'block' }} />
  ) : (msgType === 'image' || msgType === 'file') && assetUrl ? (
    // 显式下载：同源 download 生效（后端已 Content-Disposition: attachment + nosniff）；
    // 跨域 download 会被浏览器忽略，故退化为带 noopener noreferrer 的外链，不硬加 download
    assetCrossOrigin ? (
      <a href={assetUrl} target="_blank" rel="noopener noreferrer" style={{ color: 'inherit', textDecoration: 'underline' }}>
        {content || '下载文件'}
      </a>
    ) : (
      <a href={assetUrl} download rel="noopener noreferrer" style={{ color: 'inherit', textDecoration: 'underline' }}>
        {content || '下载文件'}
      </a>
    )
  ) : msgType === 'voice' && assetUrl ? (
    <div style={{ minWidth: 240 }}>
      <audio controls preload="metadata" src={assetUrl} style={{ width: '100%' }} />
      {content && <div style={{ fontSize: 12, marginTop: 4, opacity: 0.85 }}>{content}</div>}
    </div>
  ) : msgType === 'video' && assetUrl ? (
    <div style={{ minWidth: 260 }}>
      <video controls preload="metadata" src={assetUrl} style={{ maxWidth: 260, maxHeight: 220, borderRadius: 6, display: 'block' }} />
      {content && <div style={{ fontSize: 12, marginTop: 4, opacity: 0.85 }}>{content}</div>}
    </div>
  ) : (
    content
  );

  /** 访客侧情绪/意图标签（气泡上沿，默认与转写形态共用） */
  const visitorTags =
    !isRight && (emotion || intentCode) ? (
      <div style={{ textAlign: 'left', marginBottom: 2, fontSize: 11 }}>
        {emotion && (
          <PillTag size="sm" color={emotionColor as PillColor}>
            {emotionText}
          </PillTag>
        )}
        {intentCode && (
          <PillTag size="sm" color="arcoblue">
            {getIntentName(intentCode)}
          </PillTag>
        )}
      </div>
    ) : null;

  /** 机器人 KB 来源行 */
  const robotSource =
    sender === 'robot' && kbRef && (kbRef.doc_title || kbRef.qa_id) ? (
      <div style={{ fontSize: 12, marginTop: 2, opacity: 0.9, color: isRight ? 'var(--color-white)' : 'var(--color-text-3)' }}>
        来源：{kbRef.doc_title || `QA#${kbRef.qa_id}`}
        {kbRef.version ? ` · v${kbRef.version}` : ''}
        {kbRef.effective_at ? ` · 生效 ${String(kbRef.effective_at).slice(0, 10)}` : ''}
      </div>
    ) : null;

  // 工作台在线会话：与通话转写同一套气泡形态（客户蓝 / 坐席绿 / 机器人青；名字+时间在气泡上沿）
  if (variant === 'transcript') {
    const roleCls = isAgent ? ' bcc-call-bubble-agent' : sender === 'robot' ? ' bcc-call-bubble-ai' : '';
    return (
      <div style={{ display: 'flex', flexDirection: 'column', alignItems: isRight ? 'flex-end' : 'flex-start' }}>
        <Typography.Text
          type="secondary"
          style={{ fontSize: 'var(--bcc-font-xs)', lineHeight: '16px', padding: '0 2px', marginBottom: 2 }}
        >
          {isRight ? `${senderName} · ${time}` : `${time} · ${senderName}`}
        </Typography.Text>
        {visitorTags}
        <div className={`bcc-call-bubble${isRight ? ' bcc-call-bubble-right' : ''}${roleCls}`}>{body}</div>
        {robotSource}
      </div>
    );
  }

  return (
    <div className="chat-bubble-row" style={{ justifyContent: isRight ? 'flex-end' : 'flex-start' }}>
      <div
        style={{
          maxWidth: '75%',
          display: 'flex',
          flexDirection: 'column',
          alignItems: isRight ? 'flex-end' : 'flex-start',
        }}
      >
        <div className="chat-bubble-meta" style={{ textAlign: isRight ? 'right' : 'left' }}>
          {isRight ? (
            <>
              {senderName} · <span style={{ opacity: 0.85 }}>{time}</span>
            </>
          ) : (
            <>
              <span style={{ opacity: 0.85 }}>{time}</span> · {senderName}
            </>
          )}
        </div>
        {visitorTags}
        <div className={`bcc-quote-line${isRight ? ' bcc-quote-line--right' : ''}`}>
          <div
            className="chat-bubble"
            style={{
              background: colors[sender] || 'var(--color-fill-3)',
              color: isRight ? 'var(--color-white)' : 'var(--color-text-1)',
              padding: assetIsImage ? 4 : undefined,
            }}
          >
            {body}
          </div>
        </div>
        {robotSource}
      </div>
    </div>
  );
}
